import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Table } from "semantic-ui-react";
import { NoData } from "../../../../components/charts";
import { ListViewBase } from "../../../../components/listView";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { PerformanceTabSectionList } from "../../../Library/Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";
import { type CustomerPacks } from "../../types/packs";
import utils from "../../../../utils/miscellaneousUtils";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import dateTimeUtils from "utils/dateTimeUtils";

export interface Props {
  customerPackData: CustomerPacks[];
}

export const CustomerPackReportList: FC<Props> = ({ customerPackData }) => {
  const [slicedEntities, setSlicedEntities] = useState<CustomerPacks[]>([]);
  const location = useLocation();

  useEffect(() => {
    setSlicedEntities([...customerPackData].slice(0, 10));
  }, [customerPackData]);

  const columns: ColumnOption<CustomerPacks>[] = [
    {
      name: "Pack",
      width: 7,
      render(item) {
        return (
          <Tooltip
            target={
              <TextTruncate>
                <MoboLink to={`/${RouteNames.purchasedPacks}/${item.PackId}`} state={{ from: location.pathname }}>
                  {item.PackName}
                </MoboLink>
              </TextTruncate>
            }
            content={`${item.PackName}`}
          />
        );
      },
    },
    {
      name: "Expiration",
      width: 3,
      render(item) {
        return item.Expiration ? dateTimeUtils.formatEmptyDate(item.Expiration) : "Never";
      },
    },
    {
      name: "Total Licenses",
      width: 3,
      render(item) {
        if (item.IsAutoIncrement) {
          return "Auto-Increment";
        } else if (item.IsUnlimited) {
          return "Unlimited";
        }
        return utils.unlimitedNumber(item.TotalLicenses);
      },
    },
    {
      name: "Licenses Issued",
      width: 3,
      render(item) {
        return item.TotalLicensesIssued.toLocaleString();
      },
    },
  ];

  const buildTableBody = (item: CustomerPacks) => {
    return columns.map((column, index) => (
      <Table.Cell className="table-body-cell" width={column.width} key={`${column.name}_${index}`}>
        {column.render(item)}
      </Table.Cell>
    ));
  };

  const getData = (skip: number, top: number) => {
    setSlicedEntities([...customerPackData].slice(skip, skip + top));
  };

  return (
    <PerformanceTabSectionList
      title="Packs"
      count={customerPackData.length}
      renderList={(page) => {
        const parsedData = slicedEntities.map((entity) => ({
          id: entity.PackId,
          ...entity,
        }));
        return customerPackData.length ? (
          <ListViewBase
            columnOptions={columns}
            loadPage={getData}
            items={parsedData}
            itemsAmount={customerPackData.length}
            buildTableBody={buildTableBody}
            withFooter={false}
            activePage={page}
            withSelection={false}
          />
        ) : (
          <NoData />
        );
      }}
    />
  );
};
